import * as Sentry from '@sentry/nextjs';
import pino from 'pino';

// Browser-side logger. `pino` resolves to its browser build here (writes to the console).
// This module is intentionally lean and carries NO server config/secrets — nothing logged from
// the browser can leak API keys / tokens, and the server logger (@/api/utils/logger) is
// `server-only` so it can never be bundled into the client.

const captureToSentry = (o: unknown) => {
    if (o && typeof o === 'object') {
        const obj = o as Record<string, unknown>;
        const err = obj.err ?? obj.error;
        if (err) {
            Sentry.captureException(err);
            return;
        }
        Sentry.captureMessage(typeof obj.msg === 'string' ? obj.msg : 'client error', 'error');
        return;
    }
    Sentry.captureMessage(String(o), 'error');
};

const root = pino({
    // Keep the console quiet in production; still forward errors to Sentry below.
    level: process.env.NODE_ENV === 'production' ? 'warn' : 'debug',
    browser: {
        // Providing `write` makes pino pass the structured log object to these handlers.
        // `warn` is spelled out rather than left to pino's console fallback: pino resolves
        // that fallback once, when the logger is created, so a `console.warn` installed later
        // (a test spy) would never see it.
        write: {
            warn: (o) => {
                // eslint-disable-next-line no-console
                console.warn(o);
            },
            error: (o) => {
                // eslint-disable-next-line no-console
                console.error(o);
                captureToSentry(o);
            },
            fatal: (o) => {
                // eslint-disable-next-line no-console
                console.error(o);
                captureToSentry(o);
            },
        },
    },
});

export const getLogger = (name: string) => root.child({ context: name });

export default root;
